import LocationOnIcon from '@mui/icons-material/LocationOn';
import { MainLayout } from '../../Components/Layouts/MainLayout/MainLayout';
import { Box, Tab, Typography, Button } from '@mui/material';
import { DataBoxTable } from '../../Components/DataBoxTable/DataBoxTable';
import { NotesBox } from '../../Components/NotesBox/NotesBox';
import {
  AttachmentsContainer,
  Container,
  StyledAvatar,
  SubTitle,
  TabsContainer,
  Title,
  TitleContainer,
} from './styles';
import SearchableChipBox from '../../Components/SearchableChipBox/SearchableChipBox';
import { AttachmentsBox } from '../../Components/AttachmentsBox/AttachmentsBox';
import { Dropzone } from '../../Components/Dropzone/Dropzone';
import { useCandidate } from '../../Hooks/useCandidate';
import { getInitials } from '../../Utils';
import { candidateDetailsFields } from '../../Utils/fields';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createCandidateNote,
  getCandidateAreas,
  getCandidateCountries,
  getCandidateReferrals,
} from '../../Services/Candidates';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { CandidateJobList } from './CandidateJobList';
import { getCandidateJobs } from '../../Services/CandidateJobs';
import { CandidateJobAttachments } from './Components/CandidateJobAttachments';
import { useAuthStore } from '../../Stores/useAuthStore';
import { useState } from 'react';
import { format } from 'date-fns';
import { CandidateJobAttachmentsDialog } from '../../Components/Dialogs/AttachFilesDialog/CandidateJobAttachmentsDialog';
import { DocumentType } from '../../Types/constants';

interface Props {
  defaultTab: number;
}

export const CandidateDetail = ({ defaultTab }: Props) => {
  const { t } = useTranslation();
  const { candidateId } = useParams();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const {
    onEdit,
    setOnEdit,
    candidate,
    selectedTab,
    control,
    handleSubmit,
    errors,
    onSubmit,
    handleChange,
    selectedSkills,
    handleSkillsSave,
    candidateFiles,
    files,
    setFiles,
    handleFileUpload,
    loadingFiles,
    isLoadingGetFiles,
    languageFormData
  } = useCandidate(defaultTab);

  const [allExpanded, setAllExpanded] = useState(false);
  const [showUpdateAttachmentsModal, setShowUpdateAttachmentsModal] = useState(false);
  const errorMessage = t('toast.errors.something_went_wrong');

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const { data: jobsData } = useQuery({
    queryKey: ['getCandidateJobs', candidateId],
    queryFn: () => getCandidateJobs({ page: 1, count: 50, candidateId: candidateId }),
    enabled: !!candidateId,
  });

  const { data: referralSources } = useQuery({
    queryKey: ['referralSources'],
    queryFn: getCandidateReferrals,
  });

  const { data: areaSources } = useQuery({
    queryKey: ['areaSources'],
    queryFn: getCandidateAreas,
  });

  const { data: countrySources } = useQuery({
    queryKey: ['countrySources'],
    queryFn: getCandidateCountries,
  });

  const { mutate: createCandidateNoteMutation } = useMutation({
    mutationFn: (description: string) =>
      createCandidateNote(candidateId!, { description, userId: user?.id! }),
    onError: () => {
      toast.error(errorMessage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getCandidateById'] });
    },
  });

  const toggleAllJobs = () => {
    setAllExpanded((prevState) => !prevState);
  };

  return (
    <MainLayout noPadding>
      {candidate && (
        <div>
          <Container>
            <StyledAvatar>{getInitials(candidate?.fullName)}</StyledAvatar>
            <TitleContainer>
              <Title>{candidate?.fullName}</Title>
              {candidate.city && candidate.country && (
                <SubTitle>
                  <LocationOnIcon style={{ marginRight: '8px' }} />
                  {candidate?.city + ', ' + candidate?.country}
                </SubTitle>
              )}
            </TitleContainer>
          </Container>

          <TabsContainer
            value={selectedTab}
            onChange={handleChange}
            aria-label="basic tabs example"
          >
            <Tab label="Summary" {...a11yProps(0)} />
            <Tab label="Jobs" {...a11yProps(1)} />
            <Tab label="Notes" {...a11yProps(2)} />
            <Tab label="Attachments" {...a11yProps(3)} />
          </TabsContainer>
          <div style={{ display: 'flex', padding: '20px', justifyContent: 'space-between' }}>
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start' }}>
              {selectedTab === 0 && (
                <Typography variant="body2" fontStyle="italic" sx={{ textAlign: 'left' }}>
                  {t('candidates.last_updated')}{' '}
                  {candidate.editDateTime
                    ? format(new Date(candidate.editDateTime), 'dd-MM-yyyy')
                    : format(new Date(candidate.creationDateTime ?? ''), 'dd-MM-yyyy')}
                </Typography>
              )}
            </Box>
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
              {selectedTab === 1 && jobsData?.items && jobsData?.items?.length > 0 && (
                <Button variant="contained" onClick={toggleAllJobs} sx={{ marginLeft: 20 }}>
                  {allExpanded ? t('jobs.collapse_all') : t('jobs.expand_all')}
                </Button>
              )}
            </Box>
          </div>
          {selectedTab === 0 && (
            <div style={{ display: 'flex', padding: '5px' }}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <DataBoxTable
                  title="Candidate details"
                  fields={candidateDetailsFields(referralSources, areaSources, countrySources)}
                  control={control}
                  values={candidate ?? ''}
                  handleSave={() => handleSubmit(onSubmit)()}
                  handleEdit={() => setOnEdit(true)}
                  onEdit={onEdit}
                  handleCancel={() => setOnEdit(false)}
                  errors={errors}
                />
              </form>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <SearchableChipBox
                  initialSelectedChips={selectedSkills}
                  handleSave={handleSkillsSave}
                />
                <DataBoxTable
                  title="English Proficiency"
                  {...languageFormData}
                  handleAttach={() => setShowUpdateAttachmentsModal(true)}
                />
              </div>
            </div>
          )}
          {selectedTab === 1 && (
            <CandidateJobList allExpanded={allExpanded} setAllExpanded={setAllExpanded} />
          )}
          {selectedTab === 2 && (
            <NotesBox
              onSuccessEdit={() =>
                queryClient.invalidateQueries({ queryKey: ['getCandidateById'] })
              }
              containerWidth="40%"
              createNote={createCandidateNoteMutation}
              notes={candidate.notes}
            />
          )}
          {selectedTab === 3 && (
            <AttachmentsContainer>
              <Box>
                <AttachmentsBox
                  title="Attachments | Candidate"
                  files={candidateFiles || []}
                  isLoading={isLoadingGetFiles}
                />
                <Box display="flex" justifyContent="start" sx={{ paddingLeft: 1, marginTop: -2 }}>
                  <Typography variant="body2" color="gray" sx={{ textAlign: 'left' }}>
                    {t('candidates.jobs_files_note')}
                  </Typography>
                </Box>
                {jobsData?.items.map((job: any) => (
                  <CandidateJobAttachments key={job.id} candidateJob={job} />
                ))}
              </Box>
              <Dropzone
                onFileUpload={handleFileUpload}
                files={files}
                setFiles={setFiles}
                loading={loadingFiles}
              />
            </AttachmentsContainer>
          )}
          <CandidateJobAttachmentsDialog
            candidateId={candidate.id}
            open={showUpdateAttachmentsModal}
            handleClose={() => setShowUpdateAttachmentsModal(false)}
            isCandidateFile={true}
            documentType={DocumentType.EnglishDocument}
          />
        </div>
      )}
    </MainLayout>
  );
};
